/**
 * VoicemailDetails.js
 * Talkroute
 *
 * Created by Vel Genov on 2/19/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import {
  ADD_CONTACT_TOOLTIP_LABEL,
  CONTACTS_INITIAL_PAGE,
  GET_TEN_THOUSAND_CONTACTS,
} from "@components/Contacts/constants";
import ContactDialog from "@components/Contacts/ContactDialog";
import CreateContactDialog from "@components/Contacts/CreateContactDialog";
import ConfirmDialog from "@components/PresentationComponents/ConfirmDialog";
import Tooltip from "@components/PresentationComponents/FormComponents/Tooltip";
import { domIds } from "@constants/testConstants";
import validateArray from "@helpers/arrayValidator";
import { addContactNameToVoicemail } from "@helpers/functions/contacts";
import { trimPhoneNumber } from "@helpers/functions/phoneNumberFormatter";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import useMetadata from "@helpers/hooks/useMetadata.hook";
import useVoicemailSelector from "@helpers/hooks/useVoicemailSelector.hook";
import { createStyles, IconButton, makeStyles } from "@material-ui/core";
import { ContactPhoneTypeEnum, IContact } from "@models/Contacts.models";
import CreateContactIcon from "@resources/icons/ic-add-contact.svg";
import AddContactIcon from "@resources/icons/phone-plus.svg";
import CallBlockIcon from "@resources/icons/top-nav-block.svg";
import CallIcon from "@resources/icons/top-nav-call-back.svg";
import DeleteIcon from "@resources/icons/top-nav-delete.svg";
import TextIcon from "@resources/icons/top-nav-text-back.svg";
import CallUnblockIcon from "@resources/icons/top-nav-unblock.svg";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { push } from "connected-react-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { Grid, List } from "semantic-ui-react";
import {
  blockNumber,
  fetchBlockedNumbers,
  setNumberToCall,
  setOutgoingPhoneNumber,
  unblockNumber,
} from "../../actions/callActions";
import {
  createNewConversation,
  updateNewConversationPhoneNumber,
} from "../../actions/conversationActions";
import { getUserPermissions } from "../../actions/userActions";
import {
  deleteVoicemail,
  findVoicemailIndexById,
  hideVoicemailDetails,
  showVoicemailDetails,
  updateReadStatus,
} from "../../actions/voicemailActions";
import routes from "../../constants/routes";
import uiString from "../../constants/uiString";
import userConstants from "../../constants/userConstants";
import CallDetails from "../../containers/CallDetails";
import { formatSecondsWithQualifier } from "../../helpers/converters";
import {
  createWelcomeVoicemail,
  VoicemailData,
  WELCOME_VOICEMAIL,
} from "../../services/dataClasses/VoicemailData";
import { getDescriptionForId, getEditableForId } from "../../services/dataClasses/VoicemailMailbox";
import ConfirmModal from "../modal/ConfirmModal";
import ReadIndicator from "./ReadIndicator";
import { SelectedVoicemails } from "./SelectedVoicemails";
import VoicemailPlayer from "./VoicemailPlayer";
import { getEditableForNumber } from "@services/dataClasses/TalkroutePhoneNumber";

const CANT_BLOCK_NUMBER_MESSAGE = "Number can not be blocked at this time";
const BLOCK_NUMBER_SUCCESS = "The number has been blocked.";
const UNBLOCK_NUMBER_SUCCESS = "The number has been unblocked.";

/**
 * Place the delete confirmation into the view
 * The trigger to display the confirmation is when
 * state.popupOpen variable is set to true
 */
const VoicemailDeleteConfirmation = ({
  open,
  close,
  onDelete,
  voicemails,
  voicemailDetails,
}) => {
  const voicemailIndex = findVoicemailIndexById({
    voicemails,
    voicemailId: voicemailDetails,
  });
  const voicemailToDisplay = voicemails[voicemailIndex];
  const callerNumberFormatted = voicemailToDisplay?.getCallerNumberFormatted();

  return (
    <ConfirmDialog
      id={`${domIds.VOICEMAIL_DELETE_CONFIRM_DIALOG}-${String(
        voicemailDetails
      )}`}
      header={`${uiString.VOICEMAIL_DELETE_CONFIRMATION_HEADER} ${String(
        callerNumberFormatted
      )}?`}
      content={uiString.VOICEMAIL_DELETE_CONFIRMATION_BODY}
      open={open}
      confirmButtonLabel={uiString.DELETE}
      onCancel={close}
      onConfirm={onDelete}
    />
  );
};

/**
 * Display details about a voicemail
 *
 */
const VoicemailDetails = () => {
  const { addToast } = useToasts();
  const blockNumberStatus = useAppSelector(
    (state) => state.callReducer.blockNumberStatus
  );

  const voicemailDetails = useAppSelector(
    (state) => state.voicemails.voicemailDetails
  );
  const voicemails = useAppSelector((state) => state.voicemails.data);
  const _filteredVoicemails = useAppSelector(
    (state) => state.voicemails.filteredData
  );
  const relatedCalls = useAppSelector((state) => state.voicemails.relatedCalls);
  const parent = useAppSelector(
    (state) => state.voicemails.voicemailDetailsParent
  );
  const mailboxes = useAppSelector((state) => state.voicemails.mailboxes);
  const selectedOutgoingPhoneNumber = useAppSelector(
    (state) => state.preferences.selectedOutgoingPhoneNumber
  );
  const selectedOutgoingTextNumber = useAppSelector(
    (state) => state.preferences.selectedOutgoingTextNumber
  );
  const blockedNumbers = useAppSelector(
    (state) => state.callReducer.blockedNumbers
  );
  const userPermissions = useAppSelector(
    (state) => state.authentication.userPermissions
  );
  const selectedItems = useAppSelector(
    (state) => state.itemSelection.selectedVoicemails
  );
  const { selectItem } = useVoicemailSelector(voicemails);
  const callHistory = useAppSelector((state) => state.callHistory.data);
  const dispatch = useDispatch();
  const [popupOpen, setPopupOpen] = useState(false);
  // const [currentVoicemail, setCurrentVoicemail] = useState<Voicemail9Data>();
  const [isDefaultMessageSelected, setIsDefaultMessageSelected] = useState(
    false
  );
  const [isShowBlockPermissionError, setIsShowBlockPermissionError] = useState(
    false
  );
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isCreateContactDialogOpen, setIsCreateContactDialogOpen] = useState(
    false
  );
  const classes = useStyles();
  const {
    markWelcomeVoicemailAsDeleted,
    markWelcomeVoicemailAsRead,
  } = useMetadata();

  const outgoingPhoneNumbers = useAppSelector(state => state.preferences.outgoingPhoneNumbers);
  const [canCallBack, setCanCallBack] = useState(true);

  const isSingleVoicemailSelected = selectedItems.length === 1;
  const areMultipleVoicemailsSelected = selectedItems.length > 1;
  const { data: contactList } = useGetAllContactsQuery({
    params: {
      pageSize: GET_TEN_THOUSAND_CONTACTS,
      page: CONTACTS_INITIAL_PAGE,
    },
  });

  const filteredVoicemails = useMemo(
    () =>
      contactList
        ? addContactNameToVoicemail(contactList, _filteredVoicemails)
        : _filteredVoicemails,
    [contactList, _filteredVoicemails]
  );

  const selectedVMContact = useMemo(
    () => filteredVoicemails.find((vm) => vm.id === voicemailDetails)?.contact,
    [voicemailDetails, filteredVoicemails]
  );
  const isVMSavedToContacts = useMemo(() => Boolean(selectedVMContact), [
    selectedVMContact,
  ]);

  const voicemailIndex = findVoicemailIndexById({
    voicemails,
    voicemailId: voicemailDetails,
  });
  const voicemailToDisplay = voicemails[voicemailIndex] as
    | VoicemailData
    | undefined;

  const leftColumnWidth = 7;
  const rightColumnWidth = 9;
  const duration = voicemailToDisplay?.duration;
  const mailboxId = voicemailToDisplay?.mailboxId;
  const date = voicemailToDisplay?.getSimpleFormattedDateAndTime();
  const isRead = voicemailToDisplay?.getMessageReadString();
  const mailboxDescription = getDescriptionForId(mailboxId, mailboxes);
  const editable = getEditableForId(mailboxId, mailboxes);
  const callerNumberFormatted = voicemailToDisplay?.getCallerNumberFormatted();
  const renderItemCounter = selectedItems.length <= 1;
  const disableReadIndicator =
    areMultipleVoicemailsSelected || !voicemails.length || !editable;

  const titleBlockStyle = {
    cursor: disableReadIndicator ? "default" : "pointer",
    width: "fit-content",
    display: "flex",
    marginBottom: 1,
    alignItems: "center",
    flexDirection: "row",
    opacity: disableReadIndicator ? "0.45" : "1",
  };
  const titleBlockHeaderStyle = { margin: 0 };
  const buttonStyles = (isDisabled?: boolean) => ({
    padding: 10,
    bottom: 4,
    right: 2,
    opacity: isDisabled ? 0.45 : 1,
  });
  const memoizedDefaultContactData: IContact = useMemo(
    () => ({
      name: "",
      company: "",
      emails: [""],
      role: "",
      url: "",
      notes: "",
      id: mailboxId ?? "",
      phoneNumbers: [
        {
          phoneNumber: String(callerNumberFormatted),
          type: ContactPhoneTypeEnum.MOBILE,
          id: "",
        },
      ],
    }),
    [mailboxId, callerNumberFormatted]
  );

  useEffect(() => {
    const newCanCall = () => {
      const canCall = getEditableForNumber(selectedOutgoingPhoneNumber, outgoingPhoneNumbers);
      return canCall;
    };
    setCanCallBack(newCanCall);
  }, [selectedOutgoingPhoneNumber, outgoingPhoneNumbers]);

  useEffect(() => {
    loadBlockedNumbers();
    loadUserPermissions();
  }, []);

  useEffect(() => {
    // Disable the Call back button if this is the welcome voicemail
    if (voicemailDetails === WELCOME_VOICEMAIL) {
      disableDetailButtons();
    } else {
      enableDetailButtons();
    }
  }, [voicemailDetails]);

  /**
   * Show the voicemail play controls if this is the current voicemail
   */
  const showVoicemailPlaySection = () => {
    const style = { backgroundColor: "var(--color-white)" };
    return (
      <VoicemailPlayer
        key="voicemail-singleton-player"
        markReadCallback={markVoicemailAsRead}
        selectedStyle={style}
      />
    );
  };

  const openCreateContactDialog = useCallback(
    () => setIsCreateContactDialogOpen(true),
    []
  );

  const closeCreateContactDialog = useCallback(
    () => setIsCreateContactDialogOpen(false),
    []
  );

  /**
   * Mark the voicemail as read
   *
   * @param isRead is an optional boolean parameter that indicates wheather
   * the voicemail is read or unread. Read by default
   */
  const markVoicemailAsRead = (isRead = true, toggle = false) => {
    if (isSingleVoicemailSelected && !disableReadIndicator) {
      const currentVoicemail = selectedItems[0];
      const { messageRead } = currentVoicemail;

      const updatedIsRead = toggle ? !messageRead : isRead;

      if (messageRead !== updatedIsRead) {
        // Create an update action
        dispatch(
          updateReadStatus({
            voicemailId: voicemailDetails,
            readStatus: updatedIsRead,
          })
        );

        selectItem(
          currentVoicemail?.id === WELCOME_VOICEMAIL
            ? createWelcomeVoicemail(updatedIsRead, currentVoicemail.date)
            : new VoicemailData({
                ...currentVoicemail,
                messageRead: updatedIsRead,
              })
        );

        if (currentVoicemail.id === WELCOME_VOICEMAIL) {
          markWelcomeVoicemailAsRead(updatedIsRead);
        }
      }
    }
  };

  /**
   * Display details for a single voicemail
   *
   * @param voicemailId the ID of the voicemail to show details for
   */
  const showVoicemailDetailsCallback = (voicemailId: number | null) => {
    if (!voicemailId) {
      dispatch(showVoicemailDetails(callHistory, undefined, routes.VOICEMAIL));
      // selectItem(filteredVoicemails[0]);
      if (location.pathname !== routes.VOICEMAIL_DETAILS) {
        dispatch(push(routes.VOICEMAIL_DETAILS));
      }
    }

    const voicemailArrayIndex = findVoicemailIndexById({
      voicemails,
      voicemailId,
    });
    if (voicemailArrayIndex >= 0) {
      const voicemail = voicemails[voicemailArrayIndex];
      dispatch(showVoicemailDetails(callHistory, voicemail, routes.VOICEMAIL));
      selectItem(voicemail);
      dispatch(push(routes.VOICEMAIL_DETAILS));
    }
  };

  /**
   * Select the 1st voicemail, if available
   */
  const selectVoicemailByIndex = (index: number) => {
    if (validateArray(filteredVoicemails)) {
      const selectedVoicemail = filteredVoicemails[index >= 0 ? index : 0];
      if (selectedVoicemail) {
        const { id: voicemailId } = selectedVoicemail;
        showVoicemailDetailsCallback(voicemailId);
      }
    } else {
      showVoicemailDetailsCallback(null);
    }
  };

  /**
   * Delete this voicemail
   */
  const confirmDeleteVoicemail = () => {
    const currentVoicemail = selectedItems[0];
    isSingleVoicemailSelected &&
      dispatch(
        deleteVoicemail(currentVoicemail.id, () => {
          if (filteredVoicemails[0].id === currentVoicemail.id) {
            selectVoicemailByIndex(1);
          } else {
            selectVoicemailByIndex(0);
          }
          setPopupOpen(false);
          if (currentVoicemail.id === 1) {
            markWelcomeVoicemailAsDeleted();
          }
        })
      );
  };

  /**
   * Get a call ready. Pass the numberToCall to Keypad
   */
  const callBack = () => {
    const voicemail = voicemails.find(
      (voicemailElement) => voicemailDetails === voicemailElement.id
    );

    if (voicemail) {
      const { callerNumber } = voicemail;

      dispatch(setNumberToCall(callerNumber));
      dispatch(setOutgoingPhoneNumber(selectedOutgoingPhoneNumber));
      dispatch(push(routes.KEYPAD));
    }
  };

  /**
   * Create a new conversation to the callerNumber from selectedOutgoingTextNumber
   */
  const textBack = () => {
    const voicemail = voicemails.find(
      (voicemailElement) => voicemailDetails === voicemailElement.id
    );

    if (voicemail) {
      const { callerNumber } = voicemail;
      selectedOutgoingTextNumber &&
        dispatch(createNewConversation(selectedOutgoingTextNumber));
      dispatch(updateNewConversationPhoneNumber(trimPhoneNumber(callerNumber)));

      dispatch(push(routes.TEXT_MESSAGES));
    }
  };

  /**
   * Show a toast message
   *
   * @param message the message to display
   */
  const showToast = (message: string) =>
    addToast(message, { appearance: "error", autoDismiss: true });

  /**
   * Show a toast message
   *
   * @param message the message to display
   */
  const showToastSuccess = (message: string) =>
    addToast(message, { appearance: "success", autoDismiss: true });

  /**
   * Block/unblock the call for phone number
   */
  const callBlock = () => {
    const voicemail = voicemails.find(
      (voicemailElement) => voicemailDetails === voicemailElement.id
    );

    if (voicemail) {
      const { callerNumber } = voicemail;

      if (userPermissions) {
        const { itemBlockedNumber } = userPermissions;

        if (itemBlockedNumber === userConstants.permissions.EDIT) {
          // Set the blocked numbers
          const blocked = blockedNumbers.some(
            (number) => number === callerNumber
          );

          if (blocked) {
            showToastSuccess(UNBLOCK_NUMBER_SUCCESS);
            return dispatch(unblockNumber(callerNumber));
          }

          if (blockNumberStatus === "error") {
            showToast(CANT_BLOCK_NUMBER_MESSAGE);
          } else {
            showToastSuccess(BLOCK_NUMBER_SUCCESS);
          }
          return dispatch(blockNumber(callerNumber));
        }
      }

      setIsShowBlockPermissionError(true);
    }
  };

  /**
   * Draw the delete button
   */
  const deleteButton = () => (
    <Tooltip title="Delete">
      <IconButton
        id={domIds.VOICEMAIL_DETAILS_DELETE_BUTTON}
        onClick={() => setPopupOpen(true)}
        // style={{ boxShadow: "none" }}
        style={buttonStyles(!editable)}
        disabled={!editable}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the text back button
   */
  const textBackButton = () => {
    return (
      <Tooltip title="Text">
        <IconButton
          onClick={() => textBack()}
          // style={{ boxShadow: "none" }}
          style={buttonStyles(isDefaultMessageSelected)}
          disabled={isDefaultMessageSelected}
        >
          <TextIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the call back button
   */
  const callBackButton = () => {
    return (
      <Tooltip title="Call">
        <IconButton
          onClick={() => callBack()}
          // style={{ boxShadow: "none" }}
          disabled={isDefaultMessageSelected || !canCallBack}
          style={buttonStyles(isDefaultMessageSelected || !canCallBack)}
        >
          <CallIcon />
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the call block button
   */
  const callBlockButton = () => {
    const voicemail = voicemails.find(
      (voicemailElement) => voicemailDetails === voicemailElement.id
    );

    let blocked = false;
    if (voicemail) {
      const { callerNumber } = voicemail;

      blocked = blockedNumbers.some((number) => number === callerNumber);
    }

    return (
      <Tooltip title={blocked ? "Unblock" : "Block"}>
        <IconButton
          onClick={() => callBlock()}
          // style={{ boxShadow: "none" }}
          style={buttonStyles(isDefaultMessageSelected)}
          disabled={isDefaultMessageSelected}
        >
          {blocked ? <CallUnblockIcon /> : <CallBlockIcon />}
        </IconButton>
      </Tooltip>
    );
  };

  /**
   * Draw the add contact button
   */
  const addContactButton = () => (
    <Tooltip title={ADD_CONTACT_TOOLTIP_LABEL}>
      <IconButton
        onClick={() => setIsContactDialogOpen(true)}
        style={buttonStyles(isDefaultMessageSelected)}
        disabled={isDefaultMessageSelected}
      >
        <AddContactIcon />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the create contact button
   */
  const createContactButton = () => (
    <Tooltip title={selectedVMContact ? "Edit Contact" : "Create Contact"}>
      <IconButton
        onClick={openCreateContactDialog}
        style={buttonStyles(isDefaultMessageSelected)}
        disabled={isDefaultMessageSelected}
      >
        <CreateContactIcon style={{ width: 19, height: 19 }} />
      </IconButton>
    </Tooltip>
  );

  /**
   * Draw the read indicator button
   */
  const readIndicatorButton = () => {
    if (isSingleVoicemailSelected) {
      const messageRead = selectedItems[0].messageRead;

      return (
        <div style={{ marginLeft: "1em", display: "flex", marginBottom: 5 }}>
          <ReadIndicator
            messageRead={messageRead}
            toggleCallBack={() => markVoicemailAsRead(true, true)}
            indicatorStyle={classes.indicatorStyle}
            allowHover={false}
            disabled={disableReadIndicator}
            isHeader
          />
        </div>
      );
    }

    return null;
  };

  /**
   * Draw the header buttons
   */
  const drawHeaderButtons = () => (
    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      {callBackButton()}
      {textBackButton()}
      {callBlockButton()}
      {!isVMSavedToContacts && addContactButton()}
      {createContactButton()}
      {deleteButton()}
    </div>
  );

  /**
   * Update the current voicemail object reference
   */
  const updateCurrentVoicemail = () => {
    // Get the current voicemail
    if (voicemails && voicemailDetails) {
      const currentVoicemail = selectedItems[0];
      const _currentVoicemail = voicemails.find(
        (voicemail) => voicemail.id === voicemailDetails
      );

      if (_currentVoicemail) {
        if (_currentVoicemail !== currentVoicemail) {
          selectItem(_currentVoicemail);
        }
      }
    }
  };

  const enableDetailButtons = () => {
    setIsDefaultMessageSelected(false);
  };

  const disableDetailButtons = () => {
    setIsDefaultMessageSelected(true);
  };

  /**
   * Load the blocked phone numbers
   */
  const loadBlockedNumbers = () => {
    dispatch(fetchBlockedNumbers());
  };

  /**
   * Request a getting user permissions
   */
  const loadUserPermissions = () => {
    dispatch(getUserPermissions());
  };

  /**
   * Show the call block permission error dialog
   */
  const showBlockPermissionError = () => {
    if (isShowBlockPermissionError) {
      return (
        <ConfirmModal
          title={uiString.blockPermissionErrorModal.TITLE}
          desc={uiString.blockPermissionErrorModal.DESC}
          buttonLabel={uiString.OK}
          clickActionCallback={() => setIsShowBlockPermissionError(false)}
        />
      );
    }

    return null;
  };

  if (!voicemailDetails) {
    return (
      <CallDetails
        relatedCalls={relatedCalls}
        parent={parent}
        hideDetails={(p) => dispatch(hideVoicemailDetails(p))}
      />
    );
  }

  return (
    <div
      style={{
        backgroundColor: renderItemCounter
          ? "white"
          : "var(--color-light-grey)",
        display: "flex",
      }}
    >
      {renderItemCounter ? (
        <CallDetails
          key={`voicemail-details-${voicemailDetails}`}
          title={uiString.VOICEMAIL_DETAILS}
          toggleCallBack={() => markVoicemailAsRead(true, true)}
          relatedCalls={relatedCalls}
          parent={parent}
          hideDetails={(p) => dispatch(hideVoicemailDetails(p))}
          buttons={drawHeaderButtons()}
          readIndicator={readIndicatorButton()}
          titleBlockStyle={titleBlockStyle}
          titleBlockHeaderStyle={titleBlockHeaderStyle}
        >
          {voicemailToDisplay && (
            <>
              {showBlockPermissionError()}
              <List relaxed>
                <List.Item>
                  <Grid>
                    <Grid.Column
                      className="text-normal none-select-text"
                      width={leftColumnWidth}
                    >
                      {`${uiString.CALLER_NUMBER}: `}
                    </Grid.Column>
                    <Grid.Column
                      textAlign="right"
                      className="text-light"
                      width={rightColumnWidth}
                    >
                      {callerNumberFormatted}
                    </Grid.Column>
                  </Grid>
                </List.Item>
                <List.Item style={{ paddingTop: "10px" }}>
                  <Grid>
                    <Grid.Column
                      className="text-normal none-select-text"
                      width={leftColumnWidth}
                    >
                      {`${uiString.MAILBOX}: `}
                    </Grid.Column>
                    <Grid.Column
                      textAlign="right"
                      className="text-light"
                      width={rightColumnWidth}
                    >
                      {mailboxDescription}
                    </Grid.Column>
                  </Grid>
                </List.Item>
                <List.Item style={{ paddingTop: "10px" }}>
                  <Grid>
                    <Grid.Column
                      className="text-normal none-select-text"
                      width={leftColumnWidth}
                    >
                      {`${uiString.DATE_AND_TIME}: `}
                    </Grid.Column>
                    <Grid.Column
                      textAlign="right"
                      className="text-light"
                      width={rightColumnWidth}
                    >
                      {date}
                    </Grid.Column>
                  </Grid>
                </List.Item>
                <List.Item style={{ paddingTop: "10px" }}>
                  <Grid>
                    <Grid.Column
                      className="text-normal none-select-text"
                      width={leftColumnWidth}
                    >
                      {`${uiString.DURATION}: `}
                    </Grid.Column>
                    <Grid.Column
                      textAlign="right"
                      className="text-light"
                      width={rightColumnWidth}
                    >
                      {duration &&
                        formatSecondsWithQualifier(duration.toString())}
                    </Grid.Column>
                  </Grid>
                </List.Item>
                <List.Item style={{ paddingTop: "10px" }}>
                  <Grid>
                    <Grid.Column
                      className="text-normal none-select-text"
                      width={leftColumnWidth}
                    >
                      {`${uiString.STATUS}: `}
                    </Grid.Column>
                    <Grid.Column
                      textAlign="right"
                      className="text-light"
                      width={rightColumnWidth}
                    >
                      {isRead}
                    </Grid.Column>
                  </Grid>
                </List.Item>
              </List>
              {showVoicemailPlaySection()}
            </>
          )}
        </CallDetails>
      ) : (
        <SelectedVoicemails />
      )}
      <ContactDialog
        open={isContactDialogOpen}
        onClose={() => setIsContactDialogOpen(false)}
        phoneNumber={voicemailToDisplay?.callerNumber ?? ""}
      />
      <CreateContactDialog
        open={isCreateContactDialogOpen}
        onClose={closeCreateContactDialog}
        contactData={
          isVMSavedToContacts && selectedVMContact
            ? selectedVMContact
            : memoizedDefaultContactData
        }
      />
      <VoicemailDeleteConfirmation
        open={popupOpen}
        close={() => setPopupOpen(false)}
        voicemails={voicemails}
        voicemailDetails={voicemailDetails}
        onDelete={confirmDeleteVoicemail}
      />
    </div>
  );
};

const useStyles = makeStyles(
  createStyles({
    indicatorStyle: {
      height: "14px !important",
    },
  })
);

export default VoicemailDetails;
